import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { motion } from 'framer-motion';

import {
  AssetDisplayBar,
  currencyAmonuts,
  SvgIcon,
} from '../../components/Molecules';
import { CurrencyType } from '../../constants/currencies';
import { SVGIconType } from '../../constants/svgIcon';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

interface State {
  isOpen: boolean;
  maxWidth: string;
  maxHeight: string;
  aspectRatio: string;
  withCross: boolean;
  currencies: CurrencyType[];
}
interface PopupOptions {
  maxWidth?: string;
  maxHeight?: string;
  aspectRatio?: string;
  withCross?: boolean;
  currencies?: CurrencyType[];
  backdropCallback?: () => void;
}

interface PopupContextType {
  showPopup: (popupContent: ReactNode, options?: PopupOptions) => void;
  hidePopup: () => void;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

export const PopupProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<State>({
    isOpen: false,
    maxWidth: '320px',
    maxHeight: '',
    aspectRatio: '',
    withCross: false,
    currencies: [],
  });
  const [popupContent, setPopupContent] = useState<ReactNode>(null);
  const [onBackdropClick, setOnBackdropClick] = useState<
    (() => void) | undefined
  >(undefined);

  const userId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));
  const userAccount = useUserStore((store) => store.getAccount(userId ?? ''));
  const allowance = useUserStore((store) => store.getAllowance());

  const showPopup = useCallback(
    (
      content: ReactNode,
      {
        maxWidth: w = '320px',
        maxHeight: h = '',
        aspectRatio: a = '',
        withCross: c = false,
        currencies = [],
        backdropCallback,
      }: PopupOptions = {},
    ) => {
      setState((s) => ({
        ...s,
        maxWidth: w,
        maxHeight: h,
        aspectRatio: a,
        withCross: c,
        currencies: currencies,
        isOpen: true,
      }));
      setPopupContent(content);
      setOnBackdropClick(() => backdropCallback);
    },
    [],
  );

  const hidePopup = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false }));
    setPopupContent(null);
    setOnBackdropClick(undefined);
  }, []);

  const handleBackdropClick = useCallback(() => {
    if (onBackdropClick) {
      onBackdropClick();
    }
    hidePopup();
  }, [onBackdropClick, hidePopup]);

  useEffect(() => {
    const handlePopState = () => {
      handleBackdropClick();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [state.isOpen, handleBackdropClick]);

  useEffect(() => {
    if (state.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [state.isOpen]);

  const contextValue = useMemo(
    () => ({
      showPopup,
      hidePopup,
    }),
    [showPopup, hidePopup],
  );

  const assetCurrencies = useMemo(
    () =>
      currencyAmonuts(
        {
          gameStamina: allowance?.allowances.webPlan.quantities.token,
          creationStamina:
            allowance?.allowances.webPlan.quantities.creationToken,
          token: userAccount?.balance.token,
        },
        state.currencies,
      ),
    [
      state.currencies,
      allowance?.allowances.webPlan.quantities.creationToken,
      allowance?.allowances.webPlan.quantities.token,
      userAccount?.balance.token,
    ],
  );

  return (
    <PopupContext.Provider value={contextValue}>
      {children}
      {state.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 200, // POPUP_CONTEXT_ZINDEX
            touchAction: 'none',
          }}
          onClick={handleBackdropClick}
        >
          {state.withCross && (
            <div
              style={{
                position: 'absolute',
                top: 20,
                left: 20,
                zIndex: 200, // POPUP_CONTEXT_ZINDEX
                touchAction: 'none',
                cursor: 'pointer',
              }}
              onClick={handleBackdropClick}
            >
              <SvgIcon
                name={SVGIconType.Cross}
                width={24}
                height={24}
                color={'white'}
              />
            </div>
          )}
          <motion.div
            style={{
              maxWidth: state.maxWidth,
              maxHeight: state.maxHeight,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              aspectRatio: state.aspectRatio,
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            {popupContent}
            {assetCurrencies.length > 0 ? (
              <div style={{ position: 'absolute', top: -58, right: 0 }}>
                <AssetDisplayBar
                  profile={userProfile}
                  currencies={assetCurrencies}
                />
              </div>
            ) : null}
          </motion.div>
        </div>
      )}
    </PopupContext.Provider>
  );
};
